import { Platform } from 'react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSegments } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { ContextSheet } from '@/components/ui/ContextSheet';
import { WhatsNewSheetContent } from '@/components/domain/whats-new/WhatsNewSheetContent';
import { useAuth } from '@/components/providers/AuthProvider';
import { usePermissionBootstrapReady } from '@/components/providers/PermissionsBootstrap';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { APP_VERSION } from '@/constants/version';
import {
  compareVersions,
  findLatestVersionWithSubstantiveContent,
  whatsNew,
  WHATS_NEW_AUTO_SHOW_DISABLED_STORAGE_KEY,
  WHATS_NEW_LAST_SEEN_VERSION_STORAGE_KEY,
} from '@/constants/whatsNew';

const APP_SETTLE_DELAY_MS = 350;

type IWhatsNewAutoSheetPayload = { source: 'auto'; lastSeenVersion: string | null };

const waitForAppSettle = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, APP_SETTLE_DELAY_MS);
      });
    });
  });

/**
 * One-shot trigger that auto-opens the What's New sheet when a release with displayable
 * content is newer than the last seen What's New version for this platform/screen size.
 */
export function WhatsNewProvider() {
  const { isAuthenticated, isLoading, isAuthenticating } = useAuth();
  const isPermissionBootstrapReady = usePermissionBootstrapReady();
  const isLargeScreen = useIsLargeScreen();
  const { t } = useTranslation();
  const segments = useSegments();
  const isInAppRoute = segments[0] === '(app)';
  const [isOpen, setIsOpen] = useState(false);
  const [sheetPayload, setSheetPayload] = useState<IWhatsNewAutoSheetPayload>({
    source: 'auto',
    lastSeenVersion: null,
  });
  const hasCheckedRef = useRef(false);
  const shouldMarkSeenOnCloseRef = useRef(false);

  const markCurrentVersionSeen = useCallback(async () => {
    await AsyncStorage.setItem(WHATS_NEW_LAST_SEEN_VERSION_STORAGE_KEY, APP_VERSION);
  }, []);

  const handleClose = useCallback(() => {
    if (shouldMarkSeenOnCloseRef.current) {
      shouldMarkSeenOnCloseRef.current = false;
      markCurrentVersionSeen().catch((error) => {
        console.warn('[WhatsNewProvider] Failed to save what\'s-new seen version', error);
      });
    }
    setIsOpen(false);
  }, [markCurrentVersionSeen]);

  useEffect(() => {
    if (hasCheckedRef.current) return;
    if (isLoading || isAuthenticating || !isAuthenticated) return;
    if (!isPermissionBootstrapReady) return;
    if (!isInAppRoute) return;
    hasCheckedRef.current = true;
    let cancelled = false;

    const check = async () => {
      try {
        const [stored, autoShowDisabled] = await Promise.all([
          AsyncStorage.getItem(WHATS_NEW_LAST_SEEN_VERSION_STORAGE_KEY),
          AsyncStorage.getItem(WHATS_NEW_AUTO_SHOW_DISABLED_STORAGE_KEY),
        ]);

        const appVersionComparison = stored ? compareVersions(APP_VERSION, stored) : 1;
        if (appVersionComparison < 0) return;

        if (autoShowDisabled === 'true') {
          await markCurrentVersionSeen();
          return;
        }

        if (appVersionComparison === 0) return;

        const context = { platform: Platform.OS, isLargeScreen };
        const latestDisplayRelease = findLatestVersionWithSubstantiveContent(
          APP_VERSION,
          whatsNew.versions,
          context,
        );
        const displayVersionComparison = latestDisplayRelease
          ? stored
            ? compareVersions(latestDisplayRelease.version, stored)
            : 1
          : 0;

        if (displayVersionComparison <= 0) {
          await markCurrentVersionSeen();
          return;
        }

        await waitForAppSettle();
        if (cancelled) return;

        setSheetPayload({ source: 'auto', lastSeenVersion: stored });
        shouldMarkSeenOnCloseRef.current = true;
        setIsOpen(true);
      } catch (error) {
        console.warn('[WhatsNewProvider] Failed to evaluate what\'s-new state', error);
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    isAuthenticating,
    isInAppRoute,
    isLargeScreen,
    isLoading,
    isPermissionBootstrapReady,
    markCurrentVersionSeen,
  ]);

  return (
    <ContextSheet
      isOpen={isOpen}
      onClose={handleClose}
      contentComponent={WhatsNewSheetContent}
      title={t('whatsNew.title')}
      payload={sheetPayload}
    />
  );
}
